import { FilterAltOffOutlined, ReceiptLongOutlined } from "@mui/icons-material";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    TextField,
} from "@mui/material";
import {
    ChangeEvent,
    Dispatch,
    FormEvent,
    SetStateAction,
    useState,
} from "react";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import SearchableInput from "../../Dashboard/Components/SearchableInput";
import SearchBar from "../../Table/Components/SearchBar";

type Props = {
    filter: any;
    setFilter: Dispatch<SetStateAction<any>>;
    setShowBills: Dispatch<SetStateAction<any>>;
    isLoading: boolean;
};

export const fetchHamlets = (params: any = {}) => GET("/region/hamlet", params);

export const fetchPowerstations = (params: any = {}) =>
    GET("/powerstation", params);

export const BillFilter = ({
    filter,
    setFilter,
    isLoading,
    setShowBills,
}: Props) => {
    const [intermediateFilter, setIntermediateFilter] = useState({
        ...filter,
        year: "",
        month: "",
    });

    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    const [clearAll, setClearAll] = useState(false);

    /* -------------------- VALIDATION -------------------- */
    const hasDistrict =
        intermediateFilter?.district && intermediateFilter.district !== "all";

    const hasPowerstation =
        intermediateFilter?.powerstation &&
        intermediateFilter.powerstation !== "all";

    const hasMeter =
        intermediateFilter?.search && intermediateFilter.search != "";

    const isInvalidFilter =
        // nothing selected
        (!hasDistrict && !hasPowerstation && !hasMeter) ||
        // partial location is not allowed
        hasDistrict !== hasPowerstation;

    /* ---------------------------------------------------- */

    const handleChange = (ev: ChangeEvent<any>) => {
        setIntermediateFilter((f: any) => ({
            ...f,
            [ev.target.name]: ev.target.value,
        }));
    };

    const handleFilter = (ev: FormEvent<any>) => {
        ev.preventDefault();

        if (isInvalidFilter) return; // 🚫 BLOCK GENERATION

        let month, year;

        if (intermediateFilter.date) {
            [year, month] = intermediateFilter.date
                .split("-")
                .map((x: any) => parseInt(x));
        }

        setFilter({
            ...intermediateFilter,
            // meterNo: intermediateFilter.meterNo?.map((m: any) => m.meterNo),
            date: undefined,
            month,
            year,
            ignoreCompleted: true,
        });

        setShowBills(true);
    };

    return (
        <Grid item xs={12}>
            <Card elevation={0}>
                <CardContent>
                    <Grid
                        container
                        spacing={1}
                        component="form"
                        onSubmit={handleFilter}
                    >
                        {/* ALERT */}
                        <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <SearchableInput
                                    label="District"
                                    api="/region/district"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    multiple={false}
                                    clearAll={clearAll}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>
                        <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <SearchableInput
                                    label="Powerstation"
                                    api="/powerstation"
                                    dep={{
                                        district: intermediateFilter.district,
                                    }}
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    multiple={false}
                                    clearAll={clearAll}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>
                        <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                            {" "}
                            <Grid item xs={12} md={6} lg={3}>
                                {" "}
                                <SearchableInput
                                    label="Village"
                                    api="/region/village"
                                    dep={{
                                        district: intermediateFilter.district,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    multiple={false}
                                    clearAll={clearAll}
                                />{" "}
                            </Grid>{" "}
                        </MultiPermissionAuthorize>{" "}
                        <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                            {" "}
                            <Grid item xs={12} md={6} lg={3}>
                                {" "}
                                <UpdatedSearchableInput
                                    label="Hamlet"
                                    api="/region/hamlet"
                                    dep={[
                                        intermediateFilter.village,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        village: intermediateFilter.village,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    multiple={false}
                                    clearAll={clearAll}
                                />{" "}
                            </Grid>{" "}
                        </MultiPermissionAuthorize>
                        <MultiPermissionAuthorize ops={["READ METER"]}>
                            <Grid item xs={12} md={6} lg={3}>
                                <SearchBar
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    label={"Meter No."}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>
                        <Grid item xs={12} md={6} lg={3}>
                            <TextField
                                fullWidth
                                name="date"
                                label="Billing Month"
                                type="month"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={intermediateFilter.date}
                                onChange={handleChange}
                                inputProps={{
                                    max: currentMonth, // ✅ prevents forward months
                                    min: "2025-10",
                                }}
                            />
                        </Grid>
                        <Grid item container xs={12} lg={6} spacing={1}>
                            <Grid item xs={6}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="outlined"
                                    disabled={isInvalidFilter}
                                    endIcon={
                                        isLoading ? (
                                            <CircularProgress size="1rem" />
                                        ) : (
                                            <ReceiptLongOutlined />
                                        )
                                    }
                                >
                                    Generate
                                </Button>
                            </Grid>

                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="error"
                                    startIcon={<FilterAltOffOutlined />}
                                    onClick={() => {
                                        setClearAll(true);
                                        setTimeout(
                                            () => setClearAll(false),
                                            500
                                        );
                                        window.location.reload();
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} mt={2}>
                            <Divider />

                            {isInvalidFilter && (
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        Please select either{" "}
                                        <b>District & PowerStation</b> OR{" "}
                                        <b>Meter Number</b>.
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Grid>
    );
};
